import { ChangeEvent, useRef } from 'react';
import { AttachmentFileInputId } from 'src/constants';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  useAttachFile,
  useBreakpoint,
  useOutsideClick,
  useVisible,
} from 'src/hooks';
import { GuestAuthLinks } from 'src/pages/AuthPages/components/GuestAuthLinks';
import { AttachFromGoogleDriveButton } from 'src/v2/components/ThreadInputBox/components/AttachFromGoogleDriveButton';
import { AttachFileButton } from '../AttachFileButton';
import styles from './AddAttachmentButton.module.scss';
import { isEnterprise } from 'src/utils';

interface AddAttachmentButtonProps {
  withTitle?: boolean;
  onClose?: () => void;
}

export const AddAttachmentButton = ({
  withTitle,
  onClose,
}: AddAttachmentButtonProps) => {
  const { handleUploadedFiles } = useAttachFile();
  const { isMobile } = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isVisible: isVisibleTooltip,
    onVisibilitySet: onShowTooltip,
    onVisibilityRemove: onHideTooltip,
  } = useVisible();

  useOutsideClick(containerRef, onHideTooltip);

  const handleFileSelect = async (event: ChangeEvent | File[]) => {
    if (onClose) {
      onClose();
    }
    const files = event?.hasOwnProperty('target')
      ? ((((event as ChangeEvent).target as HTMLInputElement)?.files ||
          []) as FileList)
      : (event as File[]);

    handleUploadedFiles(files);
  };

  return (
    <div ref={containerRef} className={styles.root}>
      <AttachFileButton withTitle={withTitle} onShowTooltip={onShowTooltip} />

      {!isEnterprise && (
        <AttachFromGoogleDriveButton
          withTitle={withTitle}
          onClose={onClose}
          onShowTooltip={onShowTooltip}
        />
      )}

      <input
        id={AttachmentFileInputId}
        type="file"
        onChange={handleFileSelect}
        className={styles.attachInput}
        multiple
      />

      <ReactTooltip
        clickable
        id="attachment-tooltip"
        disableStyleInjection
        noArrow={true}
        className="nj-attachment-tooltip"
        isOpen={isVisibleTooltip}
        offset={isMobile ? 0 : 20}
        imperativeModeOnly={true}
        openOnClick
        data-e2e="attachment-tooltip"
      >
        <span>You’ll need a sign-in account to add attachment</span>
        <GuestAuthLinks dataGTM="Chat-thread-attach-file-signup" />
      </ReactTooltip>
    </div>
  );
};
